import React, { useState } from 'react';
import { PenTool, Grid3X3, Settings } from 'lucide-react';
import { Resource } from '../../../types';

interface DrawingBoardConfigFormProps {
  resource: Resource;
  config?: Record<string, any>;
  onConfigChange: (config: Partial<Resource>) => void;
}

interface Template {
  id: string;
  name: string;
  type: 'line' | 'shape' | 'pattern';
  data: any;
}

export const DrawingBoardConfigForm: React.FC<DrawingBoardConfigFormProps> = ({
  resource,
  config,
  onConfigChange
}) => {
  const mergedConfig = { ...resource, ...config };
  const [gridSize, setGridSize] = useState<string>(mergedConfig.gridSize || '10x10');
  const [tools, setTools] = useState<string[]>(mergedConfig.tools || ['crayon', 'gomme']);
  const [templates, setTemplates] = useState<Template[]>(mergedConfig.templates || []);
  const [allowInteraction, setAllowInteraction] = useState<boolean>(mergedConfig.allowInteraction !== undefined ? mergedConfig.allowInteraction : true);
  const [question, setQuestion] = useState<string>(mergedConfig.question || '');
  const [showGrid, setShowGrid] = useState<boolean>(mergedConfig.showGrid !== undefined ? mergedConfig.showGrid : true);
  const [enableDownload, setEnableDownload] = useState<boolean>(resource.enableDownload !== undefined ? resource.enableDownload : true);

  const availableGridSizes = ['5x5', '8x8', '10x10', '12x12', '15x15', '20x20'];
  const availableTools = [
    { id: 'crayon', name: 'Crayon', icon: '✏️', description: 'Dessin libre' },
    { id: 'gomme', name: 'Gomme', icon: '🧽', description: 'Effacer' },
    { id: 'règle', name: 'Règle', icon: '📏', description: 'Lignes droites' },
    { id: 'formes', name: 'Formes', icon: '🔷', description: 'Formes géométriques' },
    { id: 'texte', name: 'Texte', icon: 'T', description: 'Ajouter du texte' },
    { id: 'couleur', name: 'Couleur', icon: '🎨', description: 'Sélecteur de couleur' }
  ];

  const predefinedTemplates = [
    {
      id: 'horizontal_lines',
      name: 'Lignes horizontales',
      type: 'line' as const,
      data: { direction: 'horizontal', spacing: 2 }
    },
    {
      id: 'vertical_lines',
      name: 'Lignes verticales',
      type: 'line' as const,
      data: { direction: 'vertical', spacing: 2 }
    },
    {
      id: 'diagonal_lines',
      name: 'Lignes diagonales',
      type: 'line' as const,
      data: { direction: 'diagonal', angle: 45 }
    },
    {
      id: 'circle',
      name: 'Cercle',
      type: 'shape' as const,
      data: { shape: 'circle', radius: 20 }
    },
    {
      id: 'square',
      name: 'Carré',
      type: 'shape' as const,
      data: { shape: 'square', size: 40 }
    },
    {
      id: 'triangle',
      name: 'Triangle',
      type: 'shape' as const,
      data: { shape: 'triangle', size: 40 }
    },
    {
      id: 'grid_pattern',
      name: 'Motif de grille',
      type: 'pattern' as const,
      data: { pattern: 'grid', spacing: 3 }
    }
  ];

  // Remove the problematic useEffect that was causing infinite loops
  // Instead, we'll call onConfigChange only when explicitly needed

  const toggleTool = (toolId: string) => {
    if (tools.includes(toolId)) {
      const newTools = tools.filter(t => t !== toolId);
      setTools(newTools);
      onConfigChange({
        ...resource,
        tools: newTools
      });
    } else {
      const newTools = [...tools, toolId];
      setTools(newTools);
      onConfigChange({
        ...resource,
        tools: newTools
      });
    }
  };

  const addTemplate = (template: Template) => {
    if (!templates.find(t => t.id === template.id)) {
      const newTemplates = [...templates, template];
      setTemplates(newTemplates);
      onConfigChange({
        ...resource,
        templates: newTemplates
      });
    }
  };

  const removeTemplate = (templateId: string) => {
    const newTemplates = templates.filter(t => t.id !== templateId);
    setTemplates(newTemplates);
    onConfigChange({
      ...resource,
      templates: newTemplates
    });
  };

  const getGridDimensions = () => {
    const [rows, cols] = gridSize.split('x').map(Number);
    return { rows, cols };
  };

  const { rows, cols } = getGridDimensions();

  return (
    <div className="space-y-6">
      {/* Question Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question ou consigne
        </label>
        <textarea
          value={question}
          onChange={(e) => {
            const newQuestion = e.target.value;
            setQuestion(newQuestion);
            onConfigChange({
              ...resource,
              question: newQuestion
            });
          }}
          placeholder="Ex: Dessinez les formes géométriques demandées"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
        />
      </div>

      {/* Grid Configuration */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
          <Grid3X3 className="w-5 h-5 mr-2" />
          Configuration de la grille
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Taille de la grille
            </label>
            <select
              value={gridSize}
              onChange={(e) => {
                const newGridSize = e.target.value;
                setGridSize(newGridSize);
                onConfigChange({
                  ...resource,
                  gridSize: newGridSize
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {availableGridSizes.map(size => (
                <option key={size} value={size}>
                  {size} ({size.split('x')[0]} × {size.split('x')[1]})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Afficher la grille
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => {
                  const newShowGrid = e.target.checked;
                  setShowGrid(newShowGrid);
                  onConfigChange({
                    ...resource,
                    showGrid: newShowGrid
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                {showGrid ? 'Oui' : 'Non'}
              </span>
            </div>
          </div>
        </div>

        {/* Grid Preview */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aperçu de la grille
          </label>
          <div className="inline-block border-2 border-gray-300 rounded-lg p-2 bg-white">
            {Array.from({ length: Math.min(rows, 8) }).map((_, rowIndex) => (
              <div key={rowIndex} className="flex">
                {Array.from({ length: Math.min(cols, 8) }).map((_, colIndex) => (
                  <div
                    key={colIndex}
                    className="w-4 h-4 border border-gray-200 bg-gray-50"
                    title={`Cellule (${rowIndex}, ${colIndex})`}
                  />
                ))}
                {cols > 8 && (
                  <div className="w-4 h-4 flex items-center justify-center text-xs text-gray-400">
                    ...
                  </div>
                )}
              </div>
            ))}
            {rows > 8 && (
              <div className="flex justify-center py-1">
                <span className="text-xs text-gray-400">...</span>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Grille {rows} × {cols} cellules ({rows * cols} cellules total)
          </p>
        </div>
      </div>

      {/* Tools Configuration */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
          <PenTool className="w-5 h-5 mr-2" />
          Outils de dessin
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {availableTools.map(tool => (
            <div
              key={tool.id}
              className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                tools.includes(tool.id)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
              onClick={() => toggleTool(tool.id)}
            >
              <div className="text-center">
                <div className="text-2xl mb-2">{tool.icon}</div>
                <div className="font-medium text-sm text-gray-900">{tool.name}</div>
                <div className="text-xs text-gray-600">{tool.description}</div>
              </div>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-gray-500 mt-2">
          Cliquez sur un outil pour l'activer/désactiver
        </p>
      </div>

      {/* Templates Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            Modèles prédéfinis
          </h3>
          <span className="text-sm text-gray-500">
            {templates.length} modèle(s) sélectionné(s)
          </span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {predefinedTemplates.map(template => (
            <div
              key={template.id}
              className={`p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                templates.find(t => t.id === template.id)
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
              onClick={() => {
                if (templates.find(t => t.id === template.id)) {
                  removeTemplate(template.id);
                } else {
                  addTemplate(template);
                }
              }}
            >
              <div className="text-center">
                <div className="text-2xl mb-2">
                  {template.type === 'line' ? '📏' :
                   template.type === 'shape' ? '🔷' :
                   template.type === 'pattern' ? '🔲' : '•'}
                </div>
                <div className="font-medium text-sm text-gray-900">{template.name}</div>
                <div className="text-xs text-gray-600 capitalize">{template.type}</div>
              </div>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-gray-500 mt-2">
          Cliquez sur un modèle pour l'ajouter/retirer
        </p>
      </div>

      {/* Advanced Settings */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Paramètres avancés</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permettre l'interaction
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={allowInteraction}
                onChange={(e) => {
                  const newAllowInteraction = e.target.checked;
                  setAllowInteraction(newAllowInteraction);
                  onConfigChange({
                    ...resource,
                    allowInteraction: newAllowInteraction
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                {allowInteraction ? 'Oui' : 'Non'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Permettre aux élèves de dessiner
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permettre le téléchargement
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={enableDownload}
                onChange={(e) => {
                  const newEnableDownload = e.target.checked;
                  setEnableDownload(newEnableDownload);
                  onConfigChange({
                    ...resource,
                    enableDownload: newEnableDownload
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                {enableDownload ? 'Oui' : 'Non'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Permettre de sauvegarder le dessin
            </p>
          </div>
        </div>
      </div>

      {/* Canvas Preview */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Aperçu du canvas</h3>
        
        <div className="p-4 border border-gray-200 rounded-lg bg-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              Canvas de dessin
            </span>
            <span className="text-xs text-gray-500">
              {rows} × {cols} cellules
            </span>
          </div>
          
          <div className="border-2 border-gray-300 rounded-lg bg-gray-50 p-4">
            <div className="text-center text-gray-500">
              <PenTool className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Zone de dessin interactive</p>
              <p className="text-xs">
                {tools.length} outil(s) disponible(s) • {templates.length} modèle(s) prêt(s)
              </p>
            </div>
          </div>
          
          <div className="mt-3 text-xs text-gray-500">
            <p>• Les élèves pourront utiliser les outils sélectionnés</p>
            <p>• La grille sera {showGrid ? 'visible' : 'masquée'} par défaut</p>
            {enableDownload && <p>• Le téléchargement du dessin sera possible</p>}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Résumé de la configuration</h4>
        <div className="text-xs text-blue-800 space-y-1">
          <p>• Grille: {gridSize} ({rows} × {cols} cellules)</p>
          <p>• {tools.length} outil(s) de dessin activé(s)</p>
          <p>• {templates.length} modèle(s) prédéfini(s) sélectionné(s)</p>
          <p>• Grille affichée: {showGrid ? 'Oui' : 'Non'}</p>
          <p>• Téléchargement: {enableDownload ? 'Activé' : 'Désactivé'}</p>
          {question && <p>• Question: "{question}"</p>}
        </div>
      </div>
    </div>
  );
};
